import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { ShieldCheck, AlertTriangle, RefreshCw, CalendarClock, CheckCircle2, Circle, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { knowledgeAPI } from '../../lib/api';

const DRAFTABLE = new Set(['pricing', 'faq', 'about']);

export default function KBCoveragePanel() {
  const [coverage, setCoverage] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingFreq, setSavingFreq] = useState(false);
  const [draft, setDraft] = useState(null);   // { topic, title, content, note }
  const [drafting, setDrafting] = useState(null); // topic key currently generating
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cov, sch] = await Promise.all([
        knowledgeAPI.getCoverage().catch(() => ({ data: null })),
        knowledgeAPI.getRescrapeSchedule().catch(() => ({ data: null })),
      ]);
      setCoverage(cov.data);
      setSchedule(sch.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeFrequency = async (freq) => {
    setSavingFreq(true);
    try {
      const r = await knowledgeAPI.setRescrapeSchedule(freq);
      setSchedule(r.data);
      toast.success(freq === 'off' ? 'Auto re-scrape turned off' : `Auto re-scrape set to ${freq}`);
    } catch {
      toast.error('Could not update schedule');
    } finally { setSavingFreq(false); }
  };

  const generateDraft = async (topicKey) => {
    setDrafting(topicKey);
    try {
      const r = await knowledgeAPI.draftCoverageTopic(topicKey);
      setDraft({ topic: r.data.topic, title: r.data.title, content: r.data.content, note: r.data.note });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate a draft. Try again.');
    } finally { setDrafting(null); }
  };

  const saveDraft = async () => {
    if (!draft || (draft.content || '').trim().length < 20) {
      toast.error('Please add more detail before saving.');
      return;
    }
    setSaving(true);
    try {
      const r = await knowledgeAPI.saveCoverageTopic(draft.topic, draft.title, draft.content);
      if (r?.data?.has_unconfirmed_placeholders) {
        toast.warning(`${draft.title} saved — but it still has [CONFIRM: …] placeholders. Your AI agent may quote unconfirmed details until you fill them in.`);
      } else {
        toast.success(`${draft.title} added to your Knowledge Base`);
      }
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save. Try again.');
    } finally { setSaving(false); }
  };

  if (loading || !coverage) return null;

  const pct = coverage.coverage_pct ?? 0;
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  const creditsLow = coverage.credit_balance != null && coverage.credit_balance <= 0;

  return (
    <Card data-testid="kb-coverage-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Knowledge Coverage
            <Badge variant="secondary" className="text-xs" data-testid="kb-coverage-pct">
              {coverage.covered}/{coverage.total} topics · {pct}%
            </Badge>
          </CardTitle>

          {/* Auto re-scrape schedule */}
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground hidden sm:inline">Auto re-scrape</span>
            <Select value={schedule?.frequency || 'off'} onValueChange={changeFrequency} disabled={savingFreq}>
              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="kb-rescrape-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off" data-testid="kb-rescrape-off">Off</SelectItem>
                <SelectItem value="weekly" data-testid="kb-rescrape-weekly">Weekly</SelectItem>
                <SelectItem value="monthly" data-testid="kb-rescrape-monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>

        {/* Credits-low hint — AI drafting needs credits */}
        {creditsLow && (
          <div
            className="flex items-start gap-2 p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-200/60 dark:border-rose-800/40"
            data-testid="kb-credits-low-hint"
          >
            <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
            <p className="text-xs text-foreground/80">
              <strong>AI credits low.</strong> "Draft" needs credits — top up in{' '}
              <a href="/billing" className="underline font-medium text-rose-600 dark:text-rose-400">Billing &amp; Plans</a>{' '}
              to auto-draft Pricing/FAQ/About. You can still add content manually below.
            </p>
          </div>
        )}

        {/* Topic chips */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {coverage.topics.map((t) => (
            <div
              key={t.key}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${t.covered ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10' : 'border-amber-200 bg-amber-50/40 dark:bg-amber-950/10'}`}
              title={t.hint || 'Covered'}
              data-testid={`kb-topic-${t.key}`}
            >
              {t.covered
                ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                : <Circle className="h-4 w-4 text-amber-500 shrink-0" />}
              <span className={`flex-1 ${t.covered ? 'text-foreground/80' : 'font-medium'}`}>{t.label}</span>
              {!t.covered && DRAFTABLE.has(t.key) && (
                <Button
                  size="sm" variant="ghost"
                  className="h-6 px-2 text-xs text-primary hover:text-primary shrink-0"
                  onClick={() => generateDraft(t.key)}
                  disabled={drafting === t.key || creditsLow}
                  title={creditsLow ? 'AI credits low — top up to draft' : 'Draft this topic with AI'}
                  data-testid={`kb-draft-${t.key}`}
                >
                  {drafting === t.key
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <><Sparkles className="h-3 w-3 mr-1" /> Draft</>}
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Nudge for missing topics */}
        {coverage.missing?.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60" data-testid="kb-coverage-nudge">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm text-foreground/80">
              Your agent is missing <strong>{coverage.missing.join(', ')}</strong>. Upload these so it can answer
              buyer questions and close faster.
            </p>
          </div>
        )}

        {schedule?.last_run && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Last auto re-scrape: {new Date(schedule.last_run).toLocaleDateString()}
          </p>
        )}
      </CardContent>

      <Dialog open={!!draft} onOpenChange={(o) => { if (!o) setDraft(null); }}>
        <DialogContent className="max-w-2xl" data-testid="kb-draft-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Review AI draft — {draft?.title}
            </DialogTitle>
            <DialogDescription>{draft?.note}</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 text-xs text-foreground/80">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
            Replace every <code className="px-1 rounded bg-muted">[CONFIRM: …]</code> with your real details — this is exactly what your AI agent will tell prospects.
          </div>
          <Textarea
            value={draft?.content || ''}
            onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
            rows={16}
            className="font-mono text-xs"
            data-testid="kb-draft-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)} disabled={saving} data-testid="kb-draft-cancel">Cancel</Button>
            <Button onClick={saveDraft} disabled={saving} data-testid="kb-draft-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : 'Add to Knowledge Base'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
